import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth, useTheme } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Lock, Check, Sun, Moon } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ChangePassword = () => {
  const navigate = useNavigate();
  const { user, logout, token, refreshUser } = useAuth();
  const { theme, toggleTheme } = useTheme();
  
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState({});

  const validate = () => {
    const newErrors = {};
    
    if (!formData.currentPassword) {
      newErrors.currentPassword = 'Mot de passe actuel requis';
    }
    
    if (!formData.newPassword) {
      newErrors.newPassword = 'Nouveau mot de passe requis';
    } else if (formData.newPassword.length < 6) {
      newErrors.newPassword = 'Minimum 6 caractères';
    }
    
    if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Les mots de passe ne correspondent pas';
    }
    
    if (formData.currentPassword === formData.newPassword) {
      newErrors.newPassword = 'Le nouveau mot de passe doit être différent de l\'ancien';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) return;
    
    setLoading(true);
    try {
      await axios.put(
        `${API_URL}/auth/change-password`,
        {
          current_password: formData.currentPassword,
          new_password: formData.newPassword,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      toast.success('Mot de passe modifié avec succès!');
      
      // Si l'utilisateur devait changer son mot de passe, on le redirige vers son dashboard
      if (user?.mustChangePassword) {
        // Rafraîchir les données utilisateur pour mettre à jour mustChangePassword
        await refreshUser();
        navigate(getRedirectAfterChange());
      } else {
        navigate(-1); // Retour à la page précédente
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors du changement de mot de passe';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const getBackUrl = () => {
    if (user?.role === 'ARTISTE' || user?.role === 'MANAGER') {
      if (user?.artistSlug) {
        return `/${user.artistSlug}/account`;
      }
      return '/';
    } else if (user?.role === 'FAN') {
      if (user?.artistSlug) {
        return `/${user.artistSlug}/fan/account`;
      }
      return '/';
    } else if (user?.role === 'SUPER_ADMIN') {
      return '/admin';
    } else if (user?.role === 'APPORTEUR') {
      return '/apporteur/dashboard';
    }
    return '/';
  };

  const getRedirectAfterChange = () => {
    if (user?.role === 'ARTISTE' || user?.role === 'MANAGER') {
      // S'assurer que artistSlug existe pour la redirection
      if (user?.artistSlug) {
        return `/${user.artistSlug}/management`;
      }
      // Fallback: récupérer le slug depuis le tenant
      if (user?.tenantId) {
        return `/admin`; // Temporairement rediriger vers admin si pas de slug
      }
      return '/';
    } else if (user?.role === 'FAN') {
      if (user?.artistSlug) {
        return `/${user.artistSlug}/fan/account`;
      }
      return '/';
    } else if (user?.role === 'SUPER_ADMIN') {
      return '/admin';
    } else if (user?.role === 'APPORTEUR') {
      return '/apporteur/dashboard';
    }
    return '/';
  };

  return (
    <div className="min-h-screen py-8 px-4 hero-gradient">
      {/* Background */}
      <div 
        className="fixed inset-0 opacity-10 -z-10"
        style={{
          backgroundImage: 'url(/images/music-bg.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />
      
      {/* Theme Toggle */}
      <Button
        variant="ghost"
        size="icon"
        onClick={toggleTheme}
        className="fixed top-4 right-4 z-50"
        data-testid="theme-toggle"
      >
        {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
      </Button>
      
      {/* Back Button */}
      {!user?.mustChangePassword && (
        <Button
          variant="ghost"
          onClick={() => navigate(getBackUrl())}
          className="fixed top-4 left-4 z-50"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour
        </Button>
      )}

      <div className="max-w-md mx-auto pt-16">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-primary" />
          </div>
          <h1 className="font-syne text-2xl font-bold">Changer mon mot de passe</h1>
          {user?.mustChangePassword && (
            <p className="text-yellow-600 mt-2">
              Vous devez changer votre mot de passe pour continuer
            </p>
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="glass-card">
            <CardHeader>
              <CardTitle>Sécurité</CardTitle>
              <CardDescription>
                Choisissez un mot de passe fort pour protéger votre compte
              </CardDescription>
            </CardHeader>
            
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Mot de passe actuel *</Label>
                  <Input
                    type="password"
                    value={formData.currentPassword}
                    onChange={(e) => setFormData(p => ({ ...p, currentPassword: e.target.value }))}
                    className={errors.currentPassword ? 'border-red-500' : ''}
                    placeholder="••••••••"
                    data-testid="current-password"
                  />
                  {errors.currentPassword && (
                    <p className="text-xs text-red-500">{errors.currentPassword}</p>
                  )}
                </div>
                
                <div className="space-y-2">
                  <Label>Nouveau mot de passe *</Label>
                  <Input
                    type="password"
                    value={formData.newPassword}
                    onChange={(e) => setFormData(p => ({ ...p, newPassword: e.target.value }))}
                    className={errors.newPassword ? 'border-red-500' : ''}
                    placeholder="••••••••"
                    data-testid="new-password"
                  />
                  {errors.newPassword && (
                    <p className="text-xs text-red-500">{errors.newPassword}</p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Minimum 6 caractères
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label>Confirmer le nouveau mot de passe *</Label>
                  <Input
                    type="password"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData(p => ({ ...p, confirmPassword: e.target.value }))}
                    className={errors.confirmPassword ? 'border-red-500' : ''}
                    placeholder="••••••••"
                    data-testid="confirm-password"
                  />
                  {errors.confirmPassword && (
                    <p className="text-xs text-red-500">{errors.confirmPassword}</p>
                  )}
                </div>
                
                <Button 
                  type="submit" 
                  className="w-full btn-primary"
                  disabled={loading}
                  data-testid="submit-password"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Modification...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Changer le mot de passe
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </motion.div>

        {/* Tips */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="mt-6 p-4 bg-muted/50 rounded-lg"
        >
          <h4 className="font-medium text-sm mb-2">Conseils pour un mot de passe sécurisé:</h4>
          <ul className="text-xs text-muted-foreground space-y-1">
            <li>• Utilisez au moins 8 caractères</li>
            <li>• Mélangez lettres majuscules et minuscules</li>
            <li>• Incluez des chiffres et des symboles</li>
            <li>• Évitez les informations personnelles évidentes</li>
          </ul>
        </motion.div>
      </div>
    </div>
  );
};

export default ChangePassword;
